import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Card from "../../../components/Card";
import { API_ALLOCATED_DID, API_REMOVE_DID } from "../../../service/url";
import { getApiCall } from "../../../service/httpCall";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import ToastNotification from "../../../components/Toast";
import { errorCheck } from "../auth/auth-utils";
import exportData from "../../../utilities/export";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import "../../../assets/custom/css/custom.css";
import "../../../assets/custom/css/tabs.css";
import { TabView, TabPanel } from "primereact/tabview";
import Loader from "../../../components/partials/components/loader";
import { Dropdown } from 'primereact/dropdown';
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";

const DIDHistory = () => {
  const navigate = useNavigate();

  const [didAllocateList, setDidAllocateList] = useState([]);
  const [didRemoveList, setDidRemoveList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [didList, setDidList] = useState([]);
  const [selectedDid, setSelectedDid] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getApiCall(API_ALLOCATED_DID, {}),
      getApiCall(API_REMOVE_DID, {}),
    ])
      .then(([allocatedRes, removedRes]) => {
        setDidAllocateList(allocatedRes);
        setDidRemoveList(removedRes);
  
        setFilteredData(allocatedRes);
        const allocatedDids = extractUniqueDids(allocatedRes);
        setDidList(allocatedDids);
      })
      .catch(onError)
      .finally(() => setLoading(false));
  }, []);
  
  const extractUniqueDids = (data) => {
    return Array.from(
      new Map(
        data
          .filter(item => item.did)
          .map(item => [item.did.id, { id: item.did.id, actual_number: item.did.actual_number }])
      ).values()
    );
  };
  
  

  useEffect(() => {
    const currentList = activeIndex === 0 ? didAllocateList : didRemoveList;
    const currentDidList = extractUniqueDids(currentList);
  
    setFilteredData(currentList);
    setDidList(currentDidList);
    setSelectedDid(null); 
  }, [activeIndex, didAllocateList, didRemoveList]);
  

  useEffect(() => {
    if (toastProps) {
      setShowToast(true);
    }
  }, [toastProps]);

  const handleFilter = () => {
    const list = activeIndex === 0 ? didAllocateList : didRemoveList;
  
    const start = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
    const end = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : null;
  
    const filtered = list.filter((item) => {
      const matchDate =
        !start || !end
          ? true
          : (() => {
              const itemDate = new Date(item.req_date).getTime();
              return itemDate >= start && itemDate <= end;
            })();
  
      const matchDid = !selectedDid || item.did?.id === selectedDid;
  
      return matchDate && matchDid;
    });
  
    setFilteredData(filtered);
  };
  

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedDid(null);
    setFilteredData(activeIndex === 0 ? didAllocateList : didRemoveList);
  };
  

  const handleExport = async () => {
    setButtonDisable(true);
    await exportData("DIDHistory", setToastProps, setShowToast);
    setButtonDisable(false);
  };

  const onError = (err) => {
    if (typeof err !== "object" && err.includes("TokenExpiredError")) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.error(err);
    }
  };

  if (loading) return <Loader />;

  return (
    <>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <div className="header-title">
                <h4 className="card-title">DID History</h4>
              </div>
              {filteredData.length > 0 && (
                <Button
                  label="Export Data"
                  onClick={handleExport}
                  className="p-button-active p-button-sm"
                  style={{ marginBottom: "10px", marginLeft: "10px" }}
                  disabled={buttonDisable}
                >
                  {buttonDisable && (
                    <span
                      className="spinner-border spinner-border-sm ml-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  )}
                </Button>
              )}
            </Card.Header>

            <Card.Body className="px-0">
              <div className="d-flex align-items-center mb-3" style={{ marginLeft: "10px" }}>
                <Calendar
                  value={startDate}
                  onChange={(e) => setStartDate(e.value)}
                  placeholder="Start Date"
                  showIcon
                />
                <span className="mx-2">to</span>
                <Calendar
                  value={endDate}
                  onChange={(e) => setEndDate(e.value)}
                  placeholder="End Date"
                  showIcon
                />
                <Dropdown
                    value={selectedDid}
                    options={didList}
                    onChange={(e) => setSelectedDid(e.value)}
                    optionLabel="actual_number"
                    optionValue="id"
                    placeholder="Select DID"
                    className="mx-2 p-dropdown-sm"
                />

                <Button
                  label="Filter"
                  onClick={handleFilter}
                  className="p-button-sm p-button-primary mx-2"
                />
                <Button
                  label="Reset"
                  onClick={handleReset}
                  className="p-button-sm p-button-secondary"
                />
              </div>

              <div className="table-responsive border-bottom my-3">
                <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                  <TabPanel header="Allocated DID">
                    <DataTable value={filteredData} paginator rows={8} tableStyle={{ minWidth: "50rem" }}>
                      <Column header="#" body={(rowData, { rowIndex }) => rowIndex + 1} />
                      <Column field="did.actual_number" header="DID" />
                      <Column field="toUser.name" header="Allocated To" />
                      <Column field="fromUser.username" header="Allocated By" />
                      <Column field="voicePlan.plan_name" header="Voice Plan" />
                      <Column field="action" header="Action" />
                      <Column field="created_at" header="Created Date" body={(rowData)=>
                        new Date(rowData.created_at).toLocaleString("en-IN", { day: "2-digit",
                        month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second:
                        "2-digit", }) } >
                      </Column>
                      <Column field="updated_at" header="Updated Date" body={(rowData)=>
                        new Date(rowData.updated_at).toLocaleString("en-IN", { day: "2-digit",
                        month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second:
                        "2-digit", }) } >
                      </Column>
                    </DataTable>
                  </TabPanel>

                  <TabPanel header="Removed DID">
                    <DataTable value={filteredData} paginator rows={8} tableStyle={{ minWidth: "50rem" }}>
                      <Column header="#" body={(rowData, { rowIndex }) => rowIndex + 1} />
                      <Column field="did.actual_number" header="DID" />
                      <Column field="removeFrom.name" header="Removed From" />
                      <Column field="removeBy.username" header="Removed By" />
                      <Column field="voicePlan.plan_name" header="Voice Plan" />
                      <Column field="action" header="Action" />
                      <Column field="created_at" header="Created Date" body={(rowData)=>
                        new Date(rowData.created_at).toLocaleString("en-IN", { day: "2-digit",
                        month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second:
                        "2-digit", }) } >
                      </Column>
                      <Column field="updated_at" header="Updated Date" body={(rowData)=>
                        new Date(rowData.updated_at).toLocaleString("en-IN", { day: "2-digit",
                        month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second:
                        "2-digit", }) } >
                      </Column>
                    </DataTable>
                  </TabPanel>
                </TabView>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ToastNotification show_toast={showToast} set_show_toast={setShowToast} {...toastProps} />
    </>
  );
};

export default DIDHistory;
